import { IonRoute } from '@ionic/react';
import { Permission } from '@up2stay/up2promo-api-client';
import React from 'react';
import type { ElementType } from 'react';
import { matchPath, Navigate, Route, useLocation } from 'react-router-dom';

import { shellPaths, TABS_PREFIX } from '@/core/router/app-paths';
import { withProtectedRoute } from '@/core/router/protected-route';
import { withProtectedWorkspaceRoute } from '@/core/router/with-protected-workspace-route';
import { withRequiredEntitlement } from '@/core/router/with-required-entitlement';
import { withRequiredPermissions } from '@/core/router/with-required-permissions';
import { BillingGrantsAdminPage } from '@/routes/app/BillingGrantsAdminPage';
import { ConfirmNewEmailPage } from '@/routes/app/ConfirmNewEmailPage';
import { CreateLocationPage } from '@/routes/app/CreateLocationPage';
import { CreateWorkspacePage } from '@/routes/app/CreateWorkspacePage';
import { DashboardPage } from '@/routes/app/DashboardPage';
import { FeedbackActivityPage } from '@/routes/app/FeedbackActivityPage';
import { FeedbackComposePage } from '@/routes/app/FeedbackComposePage';
import { FeedbackFormEditorPage } from '@/routes/app/FeedbackFormEditorPage';
import { FeedbackHomePage } from '@/routes/app/FeedbackHomePage';
import { FeedbackInviteDetailPage } from '@/routes/app/FeedbackInviteDetailPage';
import { FeedbackLibraryPage } from '@/routes/app/FeedbackLibraryPage';
import { FeedbackMessageEditorPage } from '@/routes/app/FeedbackMessageEditorPage';
import { LeadDetailPage } from '@/routes/app/LeadDetailPage';
import { LeadEventDetailPage } from '@/routes/app/LeadEventDetailPage';
import { LeadsEventsPage } from '@/routes/app/LeadsEventsPage';
import { LeadsPage } from '@/routes/app/LeadsPage';
import { LocationDetailPage } from '@/routes/app/LocationDetailPage';
import { LocationsPage } from '@/routes/app/LocationsPage';
import { MoreHubPage } from '@/routes/app/MoreHubPage';
import { NotFoundPage } from '@/routes/app/NotFoundPage';
import { PeoplePage } from '@/routes/app/PeoplePage';
import { ProductDetailPage } from '@/routes/app/ProductDetailPage';
import { ProductsPage } from '@/routes/app/ProductsPage';
import { ProductsSetupWizardPage } from '@/routes/app/ProductsSetupWizardPage';
import { ProfileEditPage } from '@/routes/app/ProfileEditPage';
import { ProfileLinkCardPage } from '@/routes/app/ProfileLinkCardPage';
import { ProfilesPage } from '@/routes/app/ProfilesPage';
import { ProfileTemplateDetailPage } from '@/routes/app/ProfileTemplateDetailPage';
import { ProfileTemplatesPage } from '@/routes/app/ProfileTemplatesPage';
import { ReputationAnalyticsPage } from '@/routes/app/ReputationAnalyticsPage';
import { ReputationCompetitorsPage } from '@/routes/app/ReputationCompetitorsPage';
import { ReputationDashboardPage } from '@/routes/app/ReputationDashboardPage';
import { ReputationSetupPage } from '@/routes/app/ReputationSetupPage';
import { ReviewsPage } from '@/routes/app/ReviewsPage';
import { SupportImpersonatePage } from '@/routes/app/SupportImpersonatePage';
import { SupportSetPasswordPage } from '@/routes/app/SupportSetPasswordPage';
import { SupportToolsHubPage } from '@/routes/app/SupportToolsHubPage';
import { TeamDetailPage } from '@/routes/app/TeamDetailPage';
import { UserNotificationsPage } from '@/routes/app/UserNotificationsPage';
import { UserSettingsPage } from '@/routes/app/UserSettingsPage';
import { WhatsNewPage } from '@/routes/app/WhatsNewPage';
import { WorkspaceBillingPage } from '@/routes/app/WorkspaceBillingPage';
import { WorkspaceBillingUpgradePage } from '@/routes/app/WorkspaceBillingUpgradePage';
import { WorkspaceIntegrationsPage } from '@/routes/app/WorkspaceIntegrationsPage';
import { WorkspaceSettingsPage } from '@/routes/app/WorkspaceSettingsPage';
import { PublicProfilePage } from '@/routes/PublicProfilePage';

export {
  canonicalizeShellPath,
  canonicalizeShellPathname,
  shellPaths,
} from '@/core/router/app-paths';
export {
  type AppTab,
  getMoreTabStackRoot,
  getTabRootPath,
  isMoreTabStackRoot,
  isMoreTabSubPath,
  isTabStackRoot,
  resolveTabForPath,
  shouldScrollActiveTabToTop,
  TAB_HOME,
  TAB_LEADS,
  TAB_MORE,
  TAB_PROFILES,
  TAB_ROOTS,
} from '@/core/router/app-tabs';

const ProtectedDashboard = withProtectedRoute(
  withProtectedWorkspaceRoute(DashboardPage),
);
const ProtectedPeople = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(PeoplePage, {
      anyOf: [Permission.MEMBERS_READ, Permission.TEAMS_READ],
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedTeamDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(TeamDetailPage, {
      permission: Permission.TEAMS_READ,
      redirectTo: `${shellPaths.morePeople}?tab=teams`,
    }),
  ),
);
const ProtectedProfileTemplates = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProfileTemplatesPage, {
      permission: Permission.PROFILES_MANAGE_TEMPLATES,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedProfileTemplateDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProfileTemplateDetailPage, {
      permission: Permission.PROFILES_MANAGE_TEMPLATES,
      redirectTo: shellPaths.moreProfileTemplates,
    }),
  ),
);
const ProtectedLeads = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(LeadsPage, {
      permission: Permission.LEADS_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedLeadsEvents = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(LeadsEventsPage, {
        feature: 'feature.lead_events',
        pageTitleKey: 'leads.events.title',
      }),
      {
        permission: Permission.EVENTS_READ,
        redirectTo: shellPaths.leads,
      },
    ),
  ),
);
const ProtectedLeadDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(LeadDetailPage, {
      permission: Permission.LEADS_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedLeadEventDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(LeadEventDetailPage, {
        feature: 'feature.lead_events',
        pageTitleKey: 'leads.events.title',
      }),
      {
        permission: Permission.EVENTS_READ,
        redirectTo: shellPaths.leads,
      },
    ),
  ),
);
const ProtectedProfiles = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProfilesPage, {
      permission: Permission.PROFILES_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedProfileEdit = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProfileEditPage, {
      permission: Permission.PROFILES_UPDATE,
      redirectTo: shellPaths.profiles,
    }),
  ),
);
const ProtectedProfileLinkCard = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProfileLinkCardPage, {
      permission: Permission.PROFILES_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedProducts = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProductsPage, {
      permission: Permission.PRODUCTS_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedProductDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProductDetailPage, {
      permission: Permission.PRODUCTS_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedProductsSetup = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(ProductsSetupWizardPage, {
      permission: Permission.PRODUCTS_MANAGE,
      redirectTo: shellPaths.profiles,
    }),
  ),
);
const ProtectedUserSettings = withProtectedRoute(UserSettingsPage);
const ProtectedWhatsNew = withProtectedRoute(WhatsNewPage);
const ProtectedSupportImpersonate = withProtectedRoute(SupportImpersonatePage);
const ProtectedSupportSetPassword = withProtectedRoute(SupportSetPasswordPage);
const ProtectedSupportToolsHub = withProtectedRoute(SupportToolsHubPage);
const ProtectedBillingGrantsAdmin = withProtectedRoute(BillingGrantsAdminPage);
const ProtectedMoreHub = withProtectedRoute(MoreHubPage);
const ProtectedUserNotifications = withProtectedRoute(UserNotificationsPage);
const ProtectedConfirmNewEmail = withProtectedRoute(ConfirmNewEmailPage);
const ProtectedLocations = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(LocationsPage, {
      permission: Permission.LOCATIONS_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedCreateLocation = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(CreateLocationPage, {
      permission: Permission.LOCATIONS_MANAGE,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedCreateWorkspace = withProtectedRoute(
  withProtectedWorkspaceRoute(CreateWorkspacePage),
);
const ProtectedLocationDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(LocationDetailPage, {
      permission: Permission.LOCATIONS_READ,
      redirectTo: shellPaths.moreWorkspaceLocations,
    }),
  ),
);
const ProtectedIntegrations = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(WorkspaceIntegrationsPage, {
        feature: 'feature.integrations',
        pageTitleKey: 'workspace.integrations.title',
      }),
      {
        // Reading connection status needs `integrations:read`; connect and
        // disconnect are gated inside the page by `integrations:manage`.
        permission: Permission.INTEGRATIONS_READ,
        redirectTo: shellPaths.home,
      },
    ),
  ),
);
const ProtectedReviews = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(ReviewsPage, {
        feature: 'feature.reputation',
        pageTitleKey: 'reviews.title',
      }),
      {
        permission: Permission.REPUTATION_READ,
        redirectTo: shellPaths.home,
      },
    ),
  ),
);
const ProtectedReputationDashboard = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(ReputationDashboardPage, {
        feature: 'feature.reputation',
        pageTitleKey: 'reputation.dashboard.title',
      }),
      {
        permission: Permission.REPUTATION_READ,
        redirectTo: shellPaths.home,
      },
    ),
  ),
);
const ProtectedReputationAnalytics = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(ReputationAnalyticsPage, {
        feature: 'feature.advanced_analytics',
        pageTitleKey: 'reputation.analytics.title',
      }),
      {
        permission: Permission.REPUTATION_READ,
        redirectTo: shellPaths.home,
      },
    ),
  ),
);
const ProtectedReputationCompetitors = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(ReputationCompetitorsPage, {
        feature: 'feature.competitor_benchmark',
        pageTitleKey: 'reputation.competitorsPage.title',
      }),
      {
        permission: Permission.REPUTATION_READ,
        redirectTo: shellPaths.home,
      },
    ),
  ),
);
const ProtectedReputationSetup = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(
      withRequiredEntitlement(ReputationSetupPage, {
        feature: 'feature.reputation',
        pageTitleKey: 'reputation.setup.title',
      }),
      {
        permission: Permission.REPUTATION_CONFIGURE,
        redirectTo: shellPaths.home,
      },
    ),
  ),
);
const ProtectedFeedbackHome = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackHomePage, {
      permission: Permission.FEEDBACK_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedFeedbackActivity = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackActivityPage, {
      permission: Permission.FEEDBACK_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedFeedbackLibrary = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackLibraryPage, {
      permission: Permission.FEEDBACK_READ,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedFeedbackCompose = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackComposePage, {
      permission: Permission.FEEDBACK_INVITE,
      redirectTo: shellPaths.feedbackHome,
    }),
  ),
);
const ProtectedFeedbackInviteDetail = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackInviteDetailPage, {
      permission: Permission.FEEDBACK_READ,
      redirectTo: `${shellPaths.feedbackActivity}?tab=sent`,
    }),
  ),
);
const ProtectedFeedbackFormEditor = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackFormEditorPage, {
      permission: Permission.FEEDBACK_INVITE,
      redirectTo: shellPaths.feedbackLibrary,
    }),
  ),
);
const ProtectedFeedbackMessageEditor = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(FeedbackMessageEditorPage, {
      permission: Permission.FEEDBACK_INVITE,
      redirectTo: `${shellPaths.feedbackLibrary}?tab=messages`,
    }),
  ),
);
const ProtectedWorkspaceSettings = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(WorkspaceSettingsPage, {
      permission: Permission.WORKSPACE_MANAGE_SETTINGS,
      redirectTo: shellPaths.home,
    }),
  ),
);
const ProtectedWorkspaceBilling = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(WorkspaceBillingPage, {
      permission: Permission.BILLING_READ,
      redirectTo: shellPaths.moreWorkspaceSettings,
    }),
  ),
);
const ProtectedWorkspaceBillingUpgrade = withProtectedRoute(
  withProtectedWorkspaceRoute(
    withRequiredPermissions(WorkspaceBillingUpgradePage, {
      permission: Permission.BILLING_READ,
      redirectTo: shellPaths.moreWorkspaceSettings,
    }),
  ),
);

const ProtectedNotFound = withProtectedRoute(NotFoundPage);

/**
 * Paths of every in-shell route, read from `SHELL_TAB_ROUTES` itself so the
 * not-found gate cannot drift from the route list. Param routes match by
 * prefix, static routes exactly.
 */
function isKnownShellPath(pathname: string): boolean {
  return SHELL_TAB_ROUTES.some((route) => {
    const { path } = route.props as { path?: string };
    if (!path || path === '*') return false;
    return Boolean(
      matchPath({ path, end: path.indexOf(':') === -1 }, pathname),
    );
  });
}

function shellIonRoute(
  path: string,
  // Route components carry heterogeneous prop shapes once wrapped by the
  // protection HOC chain; IonRoute mounts the element without RR5 props.
  Component: ElementType,
): React.ReactElement {
  const RouteComponent = Component as React.ComponentType;
  return <IonRoute key={path} path={path} element={<RouteComponent />} />;
}

/**
 * The outlet renders `*` next to whatever matched, so the gate itself decides:
 * any unknown path under /tabs gets the 404 page (DEV-558; before, only
 * /tabs/more had one and e.g. /tabs/home/xyz stayed blank).
 */
function ShellNotFoundGate() {
  const { pathname } = useLocation();
  if (!pathname.startsWith(`${TABS_PREFIX}/`)) {
    return null;
  }
  if (isKnownShellPath(pathname)) {
    return null;
  }
  return <ProtectedNotFound />;
}

/** All in-shell routes as siblings in the single `IonTabs` outlet. */
const SHELL_TAB_ROUTES: React.ReactElement[] = [
  <Route
    key="tabs-root"
    path={TABS_PREFIX}
    element={<Navigate to={shellPaths.home} replace />}
  />,
  <Route
    key={shellPaths.home}
    path={shellPaths.home}
    element={<ProtectedDashboard />}
  />,
  shellIonRoute(shellPaths.profiles, ProtectedProfiles),
  <Route
    key="tabs-profile-singular"
    path={`${TABS_PREFIX}/profile`}
    element={<Navigate to={shellPaths.profiles} replace />}
  />,
  <Route
    key="profile-legacy"
    path="/profile"
    element={<Navigate to={shellPaths.profiles} replace />}
  />,
  shellIonRoute(shellPaths.profilesEdit, ProtectedProfileEdit),
  shellIonRoute(shellPaths.profilesLinkCard, ProtectedProfileLinkCard),
  shellIonRoute(shellPaths.leads, ProtectedLeads),
  shellIonRoute(
    `${shellPaths.leads}/public/:workspaceUsername/:username`,
    PublicProfilePage,
  ),
  shellIonRoute(shellPaths.leadsEvents, ProtectedLeadsEvents),
  shellIonRoute(`${shellPaths.leadsEvents}/:eventId`, ProtectedLeadEventDetail),
  shellIonRoute(`${shellPaths.leads}/:id`, ProtectedLeadDetail),
  shellIonRoute(shellPaths.moreHub, ProtectedMoreHub),
  shellIonRoute(shellPaths.moreSupport, ProtectedSupportToolsHub),
  shellIonRoute(shellPaths.moreSupportImpersonate, ProtectedSupportImpersonate),
  shellIonRoute(shellPaths.moreSupportPassword, ProtectedSupportSetPassword),
  shellIonRoute(shellPaths.moreBillingGrants, ProtectedBillingGrantsAdmin),
  shellIonRoute(shellPaths.moreProducts, ProtectedProducts),
  shellIonRoute(shellPaths.moreProductsSetup, ProtectedProductsSetup),
  shellIonRoute(
    `${shellPaths.moreProducts}/:productId`,
    ProtectedProductDetail,
  ),
  shellIonRoute(shellPaths.moreUserSettings, ProtectedUserSettings),
  shellIonRoute(shellPaths.moreWhatsNew, ProtectedWhatsNew),
  <Route
    key={shellPaths.moreUserBilling}
    path={shellPaths.moreUserBilling}
    element={<Navigate to={shellPaths.moreWorkspaceBilling} replace />}
  />,
  shellIonRoute(shellPaths.moreUserNotifications, ProtectedUserNotifications),
  shellIonRoute(shellPaths.moreConfirmNewEmail, ProtectedConfirmNewEmail),
  shellIonRoute(shellPaths.moreWorkspaceSettings, ProtectedWorkspaceSettings),
  shellIonRoute(shellPaths.morePeople, ProtectedPeople),
  shellIonRoute(`${shellPaths.morePeople}/teams/:id`, ProtectedTeamDetail),
  shellIonRoute(shellPaths.moreWorkspaceBilling, ProtectedWorkspaceBilling),
  shellIonRoute(
    shellPaths.moreWorkspaceBillingUpgrade,
    ProtectedWorkspaceBillingUpgrade,
  ),
  shellIonRoute(shellPaths.moreWorkspaceCreate, ProtectedCreateWorkspace),
  shellIonRoute(shellPaths.moreWorkspaceLocations, ProtectedLocations),
  shellIonRoute(
    shellPaths.moreWorkspaceLocationCreate,
    ProtectedCreateLocation,
  ),
  shellIonRoute(
    `${shellPaths.moreWorkspaceLocations}/:id`,
    ProtectedLocationDetail,
  ),
  shellIonRoute(shellPaths.moreWorkspaceIntegrations, ProtectedIntegrations),
  <Route
    key={shellPaths.moreWorkspaceReviews}
    path={shellPaths.moreWorkspaceReviews}
    element={<Navigate to={shellPaths.reputationInbox} replace />}
  />,
  <Route
    key={shellPaths.reputationRoot}
    path={shellPaths.reputationRoot}
    element={<Navigate to={shellPaths.reputationDashboard} replace />}
  />,
  shellIonRoute(shellPaths.reputationDashboard, ProtectedReputationDashboard),
  shellIonRoute(shellPaths.reputationInbox, ProtectedReviews),
  shellIonRoute(shellPaths.reputationAnalytics, ProtectedReputationAnalytics),
  shellIonRoute(
    shellPaths.reputationCompetitors,
    ProtectedReputationCompetitors,
  ),
  shellIonRoute(shellPaths.reputationSetup, ProtectedReputationSetup),
  <Route
    key={shellPaths.feedbackRoot}
    path={shellPaths.feedbackRoot}
    element={<Navigate to={shellPaths.feedbackHome} replace />}
  />,
  shellIonRoute(shellPaths.feedbackHome, ProtectedFeedbackHome),
  shellIonRoute(shellPaths.feedbackActivity, ProtectedFeedbackActivity),
  shellIonRoute(shellPaths.feedbackLibrary, ProtectedFeedbackLibrary),
  shellIonRoute(shellPaths.feedbackCompose, ProtectedFeedbackCompose),
  <Route
    key={shellPaths.feedbackInbox}
    path={shellPaths.feedbackInbox}
    element={
      <Navigate to={`${shellPaths.feedbackActivity}?tab=inbox`} replace />
    }
  />,
  <Route
    key={shellPaths.feedbackInvites}
    path={shellPaths.feedbackInvites}
    element={
      <Navigate to={`${shellPaths.feedbackActivity}?tab=sent`} replace />
    }
  />,
  shellIonRoute(
    `${shellPaths.feedbackInvites}/:inviteId`,
    ProtectedFeedbackInviteDetail,
  ),
  <Route
    key={shellPaths.feedbackForms}
    path={shellPaths.feedbackForms}
    element={
      <Navigate to={`${shellPaths.feedbackLibrary}?tab=forms`} replace />
    }
  />,
  shellIonRoute(shellPaths.feedbackFormNew, ProtectedFeedbackFormEditor),
  shellIonRoute(
    `${shellPaths.feedbackForms}/:templateId`,
    ProtectedFeedbackFormEditor,
  ),
  <Route
    key={shellPaths.feedbackMessages}
    path={shellPaths.feedbackMessages}
    element={
      <Navigate to={`${shellPaths.feedbackLibrary}?tab=messages`} replace />
    }
  />,
  shellIonRoute(shellPaths.feedbackMessageNew, ProtectedFeedbackMessageEditor),
  shellIonRoute(
    `${shellPaths.feedbackMessages}/:templateId`,
    ProtectedFeedbackMessageEditor,
  ),
  shellIonRoute(shellPaths.moreProfileTemplates, ProtectedProfileTemplates),
  shellIonRoute(
    `${shellPaths.moreProfileTemplates}/:templateId`,
    ProtectedProfileTemplateDetail,
  ),
  <Route
    key={shellPaths.more}
    path={shellPaths.more}
    element={<Navigate to={shellPaths.moreHub} replace />}
  />,
  <Route key="shell-not-found" path="*" element={<ShellNotFoundGate />} />,
];

export function shellTabRoutes(): React.ReactElement[] {
  return SHELL_TAB_ROUTES;
}
